import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  FormInstance,
  MenuProps,
  Row,
  Select,
} from "antd";
import { FloatNumberInput } from "../../../../../../../../../components/FloatNumberInput";
import {
  DATE_FORMAT,
  PAYER_STATUS,
} from "../../../../../../../../../lib/constants";
import { useCallback, useMemo, useState } from "react";
import { MoreOutlined } from "@ant-design/icons";
import { useButtonHandlers } from "../hooks/useButtonHandlers";
import { FormButtons } from "../../../../../../../../../components/DefaultFormButtons";
import { useGetInitialValue } from "../hooks/useGetInitialValue";
import { Loader } from "../../../../../../../../../components/Loader";
import { CustomerInputSearch } from "../../../../../../../../../components/CustomerInputSearch";
import { usePercentageRule } from "../../../../../../../../../lib/hooks/usePercentageRule";

type TPayersFormProps = {
  primaryBuyerNumber: number;
  setId: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  id: string | null | undefined;
  fetchData: () => void;
  parentId: string;
};

export const PayersForm = ({
  id,
  fetchData,
  setId,
  parentId,
  primaryBuyerNumber,
}: TPayersFormProps) => {
  const [form]: FormInstance[] = Form.useForm();

  const [key, setKey] = useState<boolean>(false);

  const onDropdownChange = useCallback(() => {
    form.setFieldValue(["payerNumber"], primaryBuyerNumber);
    setKey(!key);
  }, [form, primaryBuyerNumber, key]);

  const items: MenuProps["items"] = useMemo(
    () => [
      {
        key: "1",
        label: <div onClick={onDropdownChange}>Set Primary Buyer</div>,
      },
    ],
    [onDropdownChange],
  );

  const { onCancelBtnClick, onDeleteBtnClick, onSaveBtnClick, isDisabled } =
    useButtonHandlers({ fetchData, setId, parentId, form });

  const { percentageRules } = usePercentageRule();

  const normalizeDate = useCallback((val: any) => val || null, []);

  const { isLoading } = useGetInitialValue(id, form);

  return (
    <>
      {isLoading ? (
        <Loader fontSize="64px" />
      ) : (
        <>
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            className="payers-form form-with-labels px-6 py-8"
            disabled={isDisabled}
          >
            <Row className="d-flex align-start">
              <CustomerInputSearch
                form={form}
                label="Payer"
                name="payerNumber"
                isFormDisabled={isDisabled}
                additionalClass="flex-grow-1"
                key={`${key}-payerNumber`}
                placeholder="Input Payer#, “Enter” to confirm"
                idItemName="payerId"
              />
              <Dropdown
                menu={{ items }}
                trigger={["click"]}
                className="ml-2 mt-6"
              >
                <Button icon={<MoreOutlined />} shape="circle" />
              </Dropdown>
            </Row>
            <FloatNumberInput
              form={form}
              name="percentage"
              label="Percentage"
              prefix="%"
              decimalLimit={4}
              className="mb-4 payers-form-percentage-input"
              rules={percentageRules}
            />
            <p className="text-md w-500 ma-0">Status</p>
            <Form.Item
              name="status"
              label="Status"
              htmlFor=""
              className="my-4 pr-2 payers-form-status-input"
            >
              <Select options={PAYER_STATUS} />
            </Form.Item>
            <Row className="w-100 d-flex">
              <Form.Item
                name="assumedDate"
                label="Assumed Date"
                className="flex-grow-1 mb-4"
                normalize={normalizeDate}
              >
                <DatePicker
                  format={DATE_FORMAT}
                  className="w-100"
                  placeholder="MM/DD/YY"
                />
              </Form.Item>
              <Form.Item
                name="soldDate"
                label="Sold Date"
                className="flex-grow-1 ml-2 mb-4"
                normalize={normalizeDate}
              >
                <DatePicker
                  format={DATE_FORMAT}
                  className="w-100"
                  placeholder="MM/DD/YY"
                />
              </Form.Item>
            </Row>
          </Form>
          {id !== undefined && (
            <FormButtons
              currentId={id}
              onSaveClick={onSaveBtnClick}
              onCancelClick={onCancelBtnClick}
              onDeleteClick={onDeleteBtnClick}
              isFormDisabled={isDisabled}
              controller="ContractPayers"
            />
          )}
        </>
      )}
    </>
  );
};
